import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { ArrowLeft, Plus, Save, Loader2, Users } from "lucide-react";
import toast from "react-hot-toast";
import {
  FIELD_REGISTRY,
  JOURNEY_SUBJECT_TYPES,
  JOURNEY_SUBJECT_TYPE_LABELS,
  JOURNEY_SEND_CLASSES,
  JOURNEY_SEND_CLASS_LABELS,
  JOURNEY_REENROLLMENTS,
  JOURNEY_REENROLLMENT_LABELS,
  type JourneySubjectType,
  type JourneyTrigger,
  type JourneyStep,
  type ConditionGroup,
  type JourneySendClass,
  type JourneyReenrollment,
} from "@ckgo2/shared";
import TriggerEditor from "./components/TriggerEditor";
import ConditionGroupEditor from "./components/ConditionGroupEditor";
import StepCard from "./components/StepCard";
import type { BuilderOptions } from "./components/ActionEditor";

interface JourneyDraft {
  name: string;
  description: string;
  enabled: boolean;
  subjectType: JourneySubjectType;
  trigger: JourneyTrigger;
  entryConditions: ConditionGroup | null;
  steps: JourneyStep[];
  reenrollment: JourneyReenrollment;
  maxConcurrentPerSubject: number;
  sendClass: JourneySendClass;
  priority: number;
}

const NEW_DRAFT: JourneyDraft = {
  name: "",
  description: "",
  enabled: false,
  subjectType: "order",
  trigger: { type: "status_change", to: [] },
  entryConditions: null,
  steps: [{ actions: [{ type: "send_email", recipient: "customer" }] }],
  reenrollment: "once",
  maxConcurrentPerSubject: 1,
  sendClass: "transactional",
  priority: 0,
};

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";
const lbl = "block text-xs font-medium text-gray-500 mb-1";
const card = "rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4";

export default function JourneyEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const [draft, setDraft] = useState<JourneyDraft>(NEW_DRAFT);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["journey", id],
    queryFn: () => api.get<ApiResponse<JourneyDraft & { id: string }>>(`/api/admin/journeys/${id}`),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing?.data) {
      const d = existing.data;
      setDraft({
        name: d.name,
        description: d.description ?? "",
        enabled: d.enabled,
        subjectType: d.subjectType,
        trigger: d.trigger,
        entryConditions: d.entryConditions ?? null,
        steps: d.steps?.length ? d.steps : NEW_DRAFT.steps,
        reenrollment: d.reenrollment,
        maxConcurrentPerSubject: d.maxConcurrentPerSubject,
        sendClass: d.sendClass,
        priority: d.priority,
      });
    }
  }, [existing]);

  // Option sources for action pickers.
  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      api.get<ApiResponse<Array<{ id: string; name?: string; email?: string }>>>(
        "/api/admin/users",
      ),
  });
  const segmentsQ = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Array<{ id: string; name: string }>>>("/api/admin/segments"),
  });
  const templatesQ = useQuery({
    queryKey: ["templates", "all"],
    queryFn: () =>
      api.get<ApiResponse<Array<{ slug: string; name: string; type: string }>>>(
        "/api/admin/templates",
      ),
  });

  const options: BuilderOptions = useMemo(
    () => ({
      users: (usersQ.data?.data ?? []).map((u) => ({ id: u.id, name: u.name || u.email || u.id })),
      segments: (segmentsQ.data?.data ?? []).map((s) => ({ id: s.id, name: s.name })),
      templates: (templatesQ.data?.data ?? [])
        .filter((t) => t.type === "email")
        .map((t) => ({ slug: t.slug, name: t.name })),
    }),
    [usersQ.data, segmentsQ.data, templatesQ.data],
  );

  const fields = FIELD_REGISTRY[draft.subjectType];

  const saveMutation = useMutation({
    mutationFn: (payload: JourneyDraft) =>
      isNew
        ? api.post<ApiResponse<{ id: string }>>("/api/admin/journeys", payload)
        : api.put<ApiResponse<{ id: string }>>(`/api/admin/journeys/${id}`, payload),
    onSuccess: () => {
      toast.success("Journey saved");
      queryClient.invalidateQueries({ queryKey: ["journeys"] });
      navigate("/journeys");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const save = () => {
    if (!draft.name.trim()) return toast.error("Name is required");
    if (draft.steps.length === 0) return toast.error("Add at least one step");
    if (draft.steps.some((s) => s.actions.length === 0))
      return toast.error("Every step needs an action");
    saveMutation.mutate(draft);
  };

  const setStep = (i: number, s: JourneyStep) =>
    setDraft((d) => ({ ...d, steps: d.steps.map((x, idx) => (idx === i ? s : x)) }));
  const moveStep = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const next = [...d.steps];
      const j = i + dir;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, steps: next };
    });

  if (!isNew && isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/journeys"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {isNew ? "New journey" : draft.name || "Edit journey"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Link
              to={`/journeys/${id}/enrollments`}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <Users className="h-4 w-4" /> Enrollments
            </Link>
          )}
          <button
            onClick={save}
            disabled={saveMutation.isPending}
            className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {/* Basics */}
      <div className={card + " space-y-3"}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className={lbl}>Name</label>
            <input
              className={sel + " w-full"}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <label className={lbl}>Subject type</label>
            <select
              className={sel + " w-full"}
              value={draft.subjectType}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  subjectType: e.target.value as JourneySubjectType,
                  trigger: { type: "status_change", to: [] },
                  entryConditions: null,
                })
              }
            >
              {JOURNEY_SUBJECT_TYPES.map((s) => (
                <option key={s} value={s}>
                  {JOURNEY_SUBJECT_TYPE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={lbl}>Description</label>
          <input
            className={sel + " w-full"}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          />
          Enabled
        </label>
      </div>

      {/* Trigger */}
      <div className={card}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Trigger</h3>
        <TriggerEditor
          value={draft.trigger}
          onChange={(trigger) => setDraft({ ...draft, trigger })}
          fields={fields}
        />
      </div>

      {/* Entry conditions */}
      <div className={card}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
          Entry conditions (optional)
        </h3>
        <ConditionGroupEditor
          value={draft.entryConditions}
          onChange={(entryConditions) => setDraft({ ...draft, entryConditions })}
          fields={fields}
        />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Steps</h3>
        {draft.steps.map((s, i) => (
          <StepCard
            key={i}
            index={i}
            total={draft.steps.length}
            step={s}
            onChange={(ns) => setStep(i, ns)}
            onRemove={() =>
              setDraft((d) => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }))
            }
            onMove={(dir) => moveStep(i, dir)}
            fields={fields}
            options={options}
          />
        ))}
        <button
          type="button"
          onClick={() =>
            setDraft((d) => ({
              ...d,
              steps: [...d.steps, { actions: [{ type: "send_email", recipient: "customer" }] }],
            }))
          }
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <Plus className="h-4 w-4" /> Add step
        </button>
      </div>

      {/* Policy */}
      <div className={card}>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Policy</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label className={lbl}>Send class</label>
            <select
              className={sel + " w-full"}
              value={draft.sendClass}
              onChange={(e) =>
                setDraft({ ...draft, sendClass: e.target.value as JourneySendClass })
              }
            >
              {JOURNEY_SEND_CLASSES.map((s) => (
                <option key={s} value={s}>
                  {JOURNEY_SEND_CLASS_LABELS[s]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              Marketing sends pass the suppression + frequency-cap gate; transactional/ops don't.
            </p>
          </div>
          <div>
            <label className={lbl}>Re-enrollment</label>
            <select
              className={sel + " w-full"}
              value={draft.reenrollment}
              onChange={(e) =>
                setDraft({ ...draft, reenrollment: e.target.value as JourneyReenrollment })
              }
            >
              {JOURNEY_REENROLLMENTS.map((r) => (
                <option key={r} value={r}>
                  {JOURNEY_REENROLLMENT_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={lbl}>Max concurrent / subject</label>
            <input
              type="number"
              min={1}
              className={sel + " w-full"}
              value={draft.maxConcurrentPerSubject}
              onChange={(e) =>
                setDraft({ ...draft, maxConcurrentPerSubject: Number(e.target.value) || 1 })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
